// ═══════════════════════════════════════════════════════════════════════════════
// Unified Report Line Parser — Single source of truth for ALL frontend rendering
// ═══════════════════════════════════════════════════════════════════════════════

export type LineKind = 'blank' | 'md-bold' | 'all-caps' | 'title-case' | 'plain';

export interface ParsedLine {
  kind: LineKind;
  /** The detected header text (with trailing colon preserved). Undefined for plain/blank. */
  header?: string;
  /** Content after the header. Undefined when no content follows. */
  body?: string;
  /** Full original line (preserves leading/trailing whitespace for round-trip fidelity). */
  raw: string;
}

/**
 * Core line parser — detects markdown bold, ALL-CAPS, and Title Case sub-headers.
 * This is the single source of truth. Every frontend render path must call this.
 *
 * Rules (evaluated in order, first match wins):
 *   1. Blank line                  → kind: 'blank'
 *   2. `**Header:**` or `**Header:** rest` → kind: 'md-bold'
 *   3. ALL-CAPS word(s) + colon  → kind: 'all-caps'  (e.g. "FINDINGS:")
 *   4. Title Case phrase + colon (no period) → kind: 'title-case'  (e.g. "Brain Parenchyma:")
 *   5. Everything else            → kind: 'plain'
 */
export function parseReportLine(line: string): ParsedLine {
  const trimmed = line.trim();
  if (!trimmed) return { kind: 'blank', raw: line };

  // 1. Markdown bold subheadings: **Header:** or **Header:** content
  // Only treated as a structural header if the bold span is short (≤60 chars) and ends with ':'.
  // Long bold spans (inline findings like **Marrow edema...**) are rendered as plain with renderInlineBold.
  const mdBold = trimmed.match(/^\*\*([^*]+?)\*\*(.*)$/);
  if (mdBold) {
    const boldText = mdBold[1];
    const rest = mdBold[2];
    if (boldText.endsWith(':') && boldText.length <= 60) {
      return {
        kind: 'md-bold',
        header: boldText,
        body: rest.trim() || undefined,
        raw: line,
      };
    }
    // Long bold span or no colon — treat as plain so renderInlineBold handles it
    return { kind: 'plain', raw: line };
  }

  // 1b. Known positive-findings headers (LLM may omit the colon).
  const positiveHeader = trimmed.match(/^(KEY ABNORMALITIES|KEY CHANGES):?\s*(.*)$/i);
  if (positiveHeader) {
    const headerText = positiveHeader[1].toUpperCase();
    return {
      kind: 'all-caps',
      header: `${headerText}:`,
      body: positiveHeader[2].trim() || undefined,
      raw: line,
    };
  }

  // 2. ALL-CAPS section headers: FINDINGS:, CLINICAL HISTORY:
  const allCaps = trimmed.match(/^([A-Z][A-Z\s/&()\u002D]{1,40}:)(.*)$/);
  if (allCaps) {
    return {
      kind: 'all-caps',
      header: allCaps[1],
      body: allCaps[2].trim() || undefined,
      raw: line,
    };
  }

  // 3. Title-case sub-headers: Brain Parenchyma:, Diffusion and Susceptibility:
  const titleCase = trimmed.match(/^([A-Z][A-Za-z\s,/&()\u002D]{2,60}:)(.*)$/);
  if (titleCase && !titleCase[1].includes('.')) {
    return {
      kind: 'title-case',
      header: titleCase[1],
      body: titleCase[2].trim() || undefined,
      raw: line,
    };
  }

  return { kind: 'plain', raw: line };
}

/**
 * Render inline **bold** spans within a text string.
 * Converts **text** → <strong>text</strong> anywhere in the string.
 */
function renderInlineBold(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/**
 * Convert parsed line to inline HTML fragment (no wrapper tags).
 * Used by formatReportContentHtml and internal helpers.
 */
export function lineToInlineHtml(parsed: ParsedLine): string {
  switch (parsed.kind) {
    case 'blank':
      return '<br>';
    case 'md-bold':
    case 'all-caps':
    case 'title-case':
      if (parsed.body) {
        return `<strong>${parsed.header}</strong> ${renderInlineBold(parsed.body)}`;
      }
      return `<strong>${parsed.header}</strong>`;
    case 'plain':
    default:
      return renderInlineBold(parsed.raw.trim());
  }
}

/**
 * Convert plain-text radiology report to HTML for clipboard / RichReportEditor.
 * Produces block-level `<p>` tags with a wrapper `<div>` for Word compatibility.
 *
 * Used by:
 *   • RichReportEditor.tsx (on-screen preview)
 *   • copyReportToClipboard() (paste into Word)
 */
export function reportTextToHtml(text: string): string {
  const body = text
    .split('\n')
    .map((line) => {
      const parsed = parseReportLine(line);
      if (parsed.kind === 'blank') return '<p><br></p>';
      return `<p>${lineToInlineHtml(parsed)}</p>`;
    })
    .join('');

  return `<div style="font-family:Arial,sans-serif;font-size:11pt;line-height:1.6">${body}</div>`;
}

/**
 * Convert plain-text radiology report to inline HTML for frontend HTML/print export.
 * Produces `<br>`-joined lines with inline `<strong>` tags — no wrapper `<div>` or `<p>`
 * tags so the parent container controls spacing and font sizing.
 *
 * Used by:
 *   • reportExport.ts buildExportHTML() (print / HTML download)
 */
export function formatReportContentHtml(text: string): string {
  return text
    .split('\n')
    .map((line) => lineToInlineHtml(parseReportLine(line)))
    .join('<br>');
}

/**
 * Parse a block of report text into structured subsections for component rendering.
 * Returns an array of { header, text } pairs suitable for MUI Typography mapping.
 *
 * Used by:
 *   • ReportEditorPage SectionCard (on-screen preview with bold headers)
 */
export interface SubsectionItem {
  type: 'subsection' | 'text';
  header?: string;
  text: string;
}

export function parseReportSubsections(text: string): SubsectionItem[] {
  const items: SubsectionItem[] = [];

  for (const line of text.split('\n')) {
    const parsed = parseReportLine(line);

    if (parsed.kind === 'blank') {
      // Preserve spacing
      items.push({ type: 'text', text: '' });
      continue;
    }

    if (parsed.kind === 'md-bold' || parsed.kind === 'all-caps' || parsed.kind === 'title-case') {
      items.push({
        type: 'subsection',
        header: parsed.header,
        text: parsed.body || '',
      });
      continue;
    }

    items.push({ type: 'text', text: parsed.raw.trim() });
  }

  return items;
}

/**
 * Copy report text to clipboard with both text/html (for Word) and text/plain fallback.
 */
export async function copyReportToClipboard(plain: string): Promise<void> {
  const html = reportTextToHtml(plain);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ]);
  } catch {
    await navigator.clipboard.writeText(plain);
  }
}
